import React, { useState, useEffect } from 'react';
import { Tree, Affix } from 'antd';
import './style.css';

/**
 * MarkdownNav组件 - 基于Markdown标题的导航组件
 * 
 * @param {Object} props - 组件属性
 * @param {string} props.content - Markdown文本内容
 * @param {string} [props.className] - 自定义CSS类名
 * @param {Object} [props.style] - 自定义内联样式
 * @param {boolean} [props.showLine=true] - 是否显示连接线
 * @param {boolean} [props.defaultExpandAll=true] - 是否默认展开所有节点
 * @param {Function} [props.onSelect] - 节点选择回调
 * @returns {JSX.Element} - Markdown导航组件
 */
const MarkdownNav = ({
  content,
  className = '',
  style = {},
  showLine = true,
  defaultExpandAll = true,
  onSelect
}) => {
  const [headings, setHeadings] = useState([]);
  const [selectedKey, setSelectedKey] = useState('');

  // 生成唯一ID - 与MarkdownRender保持一致
  const generateId = (text, level, index) => {
    // 提取文本内容，移除可能的Markdown语法
    const cleanText = text.replace(/[#*_\[\]()]/g, '').trim();
    return cleanText.toLowerCase().replace(/\s+/g, '-') || `heading-${Math.random().toString(36).substr(2, 9)}`;
  };

  // 解析Markdown内容，提取标题
  useEffect(() => {
    if (!content || content.trim() === '') {
      setHeadings([]);
      return;
    }

    // 匹配h1-h6标题，考虑可能的缩进
    const headingRegex = /^(\s*)(#{1,6})\s+(.+)$/gm;
    const extractedHeadings = [];
    let match;
    let index = 0;

    while ((match = headingRegex.exec(content)) !== null) {
      const level = match[2].length;
      const text = match[3].trim();
      const id = generateId(text, level, index);
      extractedHeadings.push({ level, text, id });
      index++;
    }

    // 构建树形结构
    const buildTree = (headings) => {
      const root = [];
      const stack = [];

      headings.forEach(heading => {
        const node = {
          title: heading.text,
          key: heading.id,
          children: []
        };

        // 找到合适的父节点
        while (stack.length > 0 && stack[stack.length - 1].level >= heading.level) {
          stack.pop();
        }

        if (stack.length === 0) {
          root.push(node);
        } else {
          stack[stack.length - 1].children.push(node);
        }

        stack.push({ ...node, level: heading.level });
      });

      return root;
    };

    const treeData = buildTree(extractedHeadings);
    setHeadings(treeData);
  }, [content]);

  // 处理节点选择
  const handleSelect = (keys) => {
    const key = keys[0];
    setSelectedKey(key);

    // 滚动到对应的标题
    const element = document.getElementById(key);
    if (element) {
      window.scrollTo({
        top: element.offsetTop - 100, // 考虑header高度
        behavior: 'smooth'
      });
    }

    // 调用自定义回调
    if (onSelect && typeof onSelect === 'function') {
      onSelect(keys);
    }
  };

  // 监听滚动事件，高亮当前可见的标题
  useEffect(() => {
    const handleScroll = () => {
      if (headings.length === 0) return;

      const scrollPosition = window.scrollY + 100;
      let currentKey = '';

      // 遍历所有标题，找到当前可见的
      const findVisibleHeading = (nodes) => {
        nodes.forEach(node => {
          const element = document.getElementById(node.key);
          if (element) {
            const top = element.offsetTop;
            if (top <= scrollPosition) {
              currentKey = node.key;
            }
          }

          if (node.children && node.children.length > 0) {
            findVisibleHeading(node.children);
          }
        });
      };

      findVisibleHeading(headings);
      if (currentKey && currentKey !== selectedKey) {
        setSelectedKey(currentKey);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [headings, selectedKey]);

  if (headings.length === 0) {
    return null;
  }

  return (
    <div className={`markdown-nav ${className}`} style={style}>
      <Affix offsetTop={100}>
        <div className="markdown-nav-content">
          <Tree
            treeData={headings}
            showLine={showLine}
            defaultExpandAll={defaultExpandAll}
            selectedKeys={[selectedKey]}
            onSelect={handleSelect}
            className="markdown-nav-tree"
          />
        </div>
      </Affix>
    </div>
  );
};

export default MarkdownNav;